/**
 * ScoreboardPanel — global DOM overlay: one board for the whole game.
 *
 * Opened from the start menu and the global toolbar (🏆). Player names
 * come from the server, so every cell is written with textContent.
 */

import { bus } from '@/core/EventBus';
import { saveManager } from '@/core/SaveManager';
import { SCORE_WEIGHTS } from '@/score/formula';
import { formatProgress, formatTime } from '@/score/format';
import { fetchLeaderboard, type BoardRow } from '@/net/leaderboardApi';

let panel: HTMLElement | null = null;
let bodyEl: HTMLElement | null = null;
let youEl: HTMLElement | null = null;
let isOpen = false;
let addedCursor = false;
let requestSeq = 0;

export function openScoreboard(): void {
  if (!panel) build();
  if (isOpen) return;
  isOpen = true;
  panel!.classList.add('open');
  addedCursor = !document.body.classList.contains('show-cursor');
  if (addedCursor) document.body.classList.add('show-cursor');
  bus.emit('game:paused', undefined);
  render();
}

export function closeScoreboard(): void {
  if (!panel || !isOpen) return;
  isOpen = false;
  panel.classList.remove('open');
  if (addedCursor) document.body.classList.remove('show-cursor');
  bus.emit('game:resumed', undefined);
}

export function toggleScoreboard(): void {
  if (isOpen) closeScoreboard(); else openScoreboard();
}

function build(): void {
  injectStyles();
  panel = document.createElement('div');
  panel.id = 'scoreboard';
  panel.innerHTML = `
    <div class="sb-card" role="dialog" aria-label="Scoreboard">
      <div class="sb-head">
        <div>
          <div class="sb-sub">Global</div>
          <h2>Scoreboard</h2>
        </div>
        <button class="sb-close" aria-label="Close">✕</button>
      </div>
      <div class="sb-you"></div>
      <div class="sb-formula"></div>
      <div class="sb-body"></div>
    </div>`;
  document.body.appendChild(panel);

  bodyEl = panel.querySelector('.sb-body');
  youEl = panel.querySelector('.sb-you');
  (panel.querySelector('.sb-formula') as HTMLElement).textContent =
    `Ranked by furthest level, then fastest finish, then score. Score = biomass ÷ ${SCORE_WEIGHTS.biomassPerPoint} ` +
    `+ ${SCORE_WEIGHTS.dna} per DNA + ${SCORE_WEIGHTS.achievement} per achievement ` +
    `+ speed bonus for each finished level.`;

  (panel.querySelector('.sb-close') as HTMLElement).addEventListener('click', closeScoreboard);
  panel.addEventListener('pointerdown', (e) => { if (e.target === panel) closeScoreboard(); });
  window.addEventListener('keydown', (e) => {
    if (isOpen && e.key === 'Escape') {
      e.stopPropagation();
      closeScoreboard();
    }
  }, { capture: true });
}

function render(): void {
  if (!bodyEl || !youEl) return;
  const player = saveManager.getPlayer();

  youEl.textContent = '';
  youEl.append('You are ');
  const b = document.createElement('b');
  b.textContent = player.playerName;
  youEl.append(b);
  if (saveManager.getMeta().scoreProgress.unranked) youEl.append(' · current run is unranked (jumped to a level)');

  bodyEl.textContent = 'Loading…';
  bodyEl.className = 'sb-body sb-msg';
  const seq = ++requestSeq;
  fetchLeaderboard(player.playerId)
    .then((data) => {
      if (seq !== requestSeq || !bodyEl) return;
      bodyEl.className = 'sb-body';
      bodyEl.textContent = '';
      if (data.rows.length === 0) {
        bodyEl.classList.add('sb-msg');
        bodyEl.textContent = 'No scores yet. Start playing to get on the board.';
        return;
      }
      bodyEl.appendChild(buildTable(data.rows, data.me, player.playerId));
    })
    .catch((err) => {
      if (seq !== requestSeq || !bodyEl) return;
      console.warn('[Scoreboard] load failed', err);
      bodyEl.className = 'sb-body sb-msg';
      bodyEl.textContent = 'Scoreboard unavailable right now.';
    });
}

function buildTable(rows: BoardRow[], me: BoardRow | null, myId: string): HTMLTableElement {
  const table = document.createElement('table');
  const head = table.createTHead().insertRow();
  const cols: [string, string][] = [
    ['#', 'num'], ['Player', ''], ['Progress', ''], ['Finish time', 'num'], ['Score', 'num'],
    ['DNA', 'num sb-hide-sm'], ['Achiev.', 'num sb-hide-sm'],
  ];
  for (const [label, cls] of cols) {
    const th = document.createElement('th');
    th.textContent = label;
    th.className = cls;
    head.appendChild(th);
  }
  const tbody = table.createTBody();
  const addRow = (r: BoardRow): void => {
    const tr = tbody.insertRow();
    if (r.playerId === myId) tr.classList.add('me');
    if (r.rank <= 3) tr.classList.add(`top${r.rank}`);
    const cells: [string, string][] = [
      [String(r.rank), 'num rank'],
      [r.name, 'name'],
      [formatProgress(r.tier, r.cellMaxBiomass, r.multicellMaxBiomass), `progress tier${r.tier}`],
      [r.finishTimeSec != null ? formatTime(r.finishTimeSec) : '—', 'num'],
      [r.score.toLocaleString(), 'num score'],
      [r.dna.toLocaleString(), 'num sb-hide-sm'],
      [String(r.achievements), 'num sb-hide-sm'],
    ];
    for (const [text, cls] of cells) {
      const td = tr.insertCell();
      td.textContent = text;
      td.className = cls;
    }
  };
  for (const r of rows) addRow(r);
  if (me && !rows.some((r) => r.playerId === me.playerId)) {
    const gap = tbody.insertRow();
    gap.className = 'gap';
    const td = gap.insertCell();
    td.colSpan = cols.length;
    td.textContent = '⋯';
    addRow(me);
  }
  return table;
}

function injectStyles(): void {
  if (document.getElementById('sb-styles')) return;
  const style = document.createElement('style');
  style.id = 'sb-styles';
  style.textContent = `
#scoreboard {
  position: fixed; inset: 0; z-index: 9600;
  display: none; align-items: center; justify-content: center;
  background: rgba(1, 3, 10, 0.6);
  font-family: 'Inter', system-ui, sans-serif; color: #e4ecf7;
}
#scoreboard.open { display: flex; }
#scoreboard .sb-card {
  width: min(680px, calc(100vw - 32px)); max-height: calc(100vh - 32px);
  box-sizing: border-box; display: flex; flex-direction: column;
  padding: 20px 22px; border-radius: 20px;
  background: rgba(10, 16, 30, 0.95); border: 1px solid rgba(79, 209, 197, 0.28);
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.6);
}
#scoreboard .sb-head { display: flex; justify-content: space-between; align-items: flex-start; }
#scoreboard .sb-sub { font-size: 10px; letter-spacing: 0.35em; text-transform: uppercase; opacity: 0.5; }
#scoreboard h2 { margin: 2px 0 0; font-size: 24px; letter-spacing: 0.08em; color: #4FD1C5; }
#scoreboard .sb-close {
  appearance: none; border: 1px solid rgba(255,255,255,0.14); background: rgba(255,255,255,0.04);
  color: #e4ecf7; width: 32px; height: 32px; border-radius: 8px; cursor: pointer; font-size: 14px;
}
#scoreboard .sb-you { margin: 10px 0 4px; font-size: 13px; opacity: 0.8; }
#scoreboard .sb-you b { color: #fff; }
#scoreboard .sb-formula { margin: 4px 0 10px; font-size: 11px; line-height: 1.5; opacity: 0.55; }
#scoreboard .sb-body { overflow-y: auto; min-height: 120px; }
#scoreboard .sb-body.sb-msg { display: flex; align-items: center; justify-content: center; opacity: 0.6; font-size: 13px; text-align: center; }
#scoreboard table { width: 100%; border-collapse: collapse; font-size: 13px; font-variant-numeric: tabular-nums; }
#scoreboard th {
  position: sticky; top: 0; background: rgb(12, 18, 32); text-align: left;
  font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; opacity: 0.6; padding: 6px 8px;
}
#scoreboard td { padding: 7px 8px; border-top: 1px solid rgba(255,255,255,0.06); white-space: nowrap; }
#scoreboard .num { text-align: right; }
#scoreboard td.name { max-width: 190px; overflow: hidden; text-overflow: ellipsis; }
#scoreboard td.score { font-weight: 600; color: #fff; }
#scoreboard td.progress { font-size: 12px; opacity: 0.85; }
#scoreboard td.progress.tier1 { color: #8fe3d9; opacity: 1; }
#scoreboard td.progress.tier2 { color: #fadf7c; opacity: 1; }
#scoreboard tr.top1 td.rank { color: #fadf7c; }
#scoreboard tr.top2 td.rank { color: #d6dde6; }
#scoreboard tr.top3 td.rank { color: #e0a36a; }
#scoreboard tr.me td { background: rgba(79, 209, 197, 0.16); color: #fff; }
#scoreboard tr.gap td { text-align: center; opacity: 0.4; border: none; padding: 2px; }
@media (max-width: 520px), (max-height: 500px) {
  #scoreboard .sb-card { padding: 14px; }
  #scoreboard .sb-hide-sm { display: none; }
  #scoreboard td.name { max-width: 120px; }
}
`;
  document.head.appendChild(style);
}
